// src/pages/WarningPage.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import api from '../services/api';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  Switch,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';

const DAYS_OPTIONS = [7, 14, 30, 60, 90, 180, 360];

function formatDateTime(dt) {
  if (!dt) return '-';
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return String(dt);
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function normStatus(s) {
  return String(s || '').trim().toLowerCase();
}

export default function WarningPage() {
  // ✅ now fixed in frontend
  const [days, setDays] = useState(30);
  const [search, setSearch] = useState('');

  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [warnings, setWarnings] = useState([]);

  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [loadingWarnings, setLoadingWarnings] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const [openDialog, setOpenDialog] = useState(false);
  const [editingWarning, setEditingWarning] = useState(null);

  const [formTitle, setFormTitle] = useState('');
  const [formDetails, setFormDetails] = useState('');
  const [formSeverity, setFormSeverity] = useState('');
  const [formIsActive, setFormIsActive] = useState(true);

  // ✅ force refetch when it changes
  const [refreshSeq, setRefreshSeq] = useState(0);

  const lastLoadRef = useRef({ employeesKey: '', warningsKey: '' });

  const selectedEmployee = useMemo(
    () => employees.find((e) => e.id === selectedEmployeeId) || null,
    [employees, selectedEmployeeId]
  );

  const activeWarningsCount = useMemo(
    () => warnings.filter((w) => w.is_active).length,
    [warnings]
  );

  const headerGradientBorder = {
    borderRadius: 2,
    borderLeft: '4px solid transparent',
    backgroundImage: `
      linear-gradient(#fff, #fff),
      linear-gradient(180deg, #ec4899, #a855f7)
    `,
    backgroundOrigin: 'border-box',
    backgroundClip: 'padding-box, border-box',
  };

  const selectedLabel = selectedEmployee
    ? `${selectedEmployee.avatar_emoji || '🙂'} ${selectedEmployee.name} (ID ${selectedEmployee.id})`
    : '—';

  const hardReload = useCallback(() => {
    setRefreshSeq((v) => v + 1);
  }, []);

  // Reset data when days changes
  useEffect(() => {
    if (!Number.isFinite(days) || days <= 0) return;
    setEmployees([]);
    setWarnings([]);
    setSelectedEmployeeId(null);
    lastLoadRef.current.employeesKey = '';
    lastLoadRef.current.warningsKey = '';
    setErrorMsg('');
  }, [days]);

  // Load employees list
  useEffect(() => {
    let mounted = true;
    if (!Number.isFinite(days) || days <= 0) return;

    const key = `${days}|${search}|${refreshSeq}`;
    if (lastLoadRef.current.employeesKey === key) return;
    lastLoadRef.current.employeesKey = key;

    (async () => {
      setLoadingEmployees(true);
      setErrorMsg('');
      try {
        const res = await api.get('/admin/warnings/employees', {
          params: {
            days,
            search,
            status: 'active', // backend can ignore; frontend will enforce anyway
          },
        });

        if (!mounted) return;

        let list = res.data?.employees || [];

        // ✅ enforce active only
        list = list.filter((e) => normStatus(e.status) === 'active');

        // ✅ sort highest warnings first, then name
        list.sort((a, b) => {
          const ca = Number(a.warning_count_window || 0);
          const cb = Number(b.warning_count_window || 0);
          if (cb !== ca) return cb - ca;
          return String(a.name || '').localeCompare(String(b.name || ''));
        });

        setEmployees(list);

        if (!list.length) {
          setSelectedEmployeeId(null);
        } else if (!selectedEmployeeId || !list.some((x) => x.id === selectedEmployeeId)) {
          setSelectedEmployeeId(list[0].id);
        }
      } catch (err) {
        if (!mounted) return;
        setErrorMsg(err?.response?.data?.msg || 'Could not load employees.');
      } finally {
        if (mounted) setLoadingEmployees(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [days, search, refreshSeq, selectedEmployeeId]);

  // Load warnings for selected employee
  useEffect(() => {
    let mounted = true;
    if (!Number.isFinite(days) || days <= 0) return;

    if (!selectedEmployeeId) {
      setWarnings([]);
      return;
    }

    const key = `${selectedEmployeeId}|${refreshSeq}`;
    if (lastLoadRef.current.warningsKey === key) return;
    lastLoadRef.current.warningsKey = key;

    (async () => {
      setLoadingWarnings(true);
      setErrorMsg('');
      try {
        const res = await api.get('/admin/warnings', {
          params: { employee_id: selectedEmployeeId },
        });
        if (!mounted) return;
        setWarnings(res.data?.warnings || []);
      } catch (err) {
        if (!mounted) return;
        setErrorMsg(err?.response?.data?.msg || 'Could not load warnings.');
      } finally {
        if (mounted) setLoadingWarnings(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [days, selectedEmployeeId, refreshSeq]);

  function openCreate() {
    if (!selectedEmployeeId) return;
    setEditingWarning(null);
    setFormTitle('');
    setFormDetails('');
    setFormSeverity('');
    setFormIsActive(true);
    setOpenDialog(true);
  }

  function openEdit(w) {
    setEditingWarning(w);
    setFormTitle(w?.title || '');
    setFormDetails(w?.details || '');
    setFormSeverity(w?.severity || '');
    setFormIsActive(!!w?.is_active);
    setOpenDialog(true);
  }

  async function submitDialog() {
    if (!selectedEmployeeId) return;

    const payload = {
      employee_id: selectedEmployeeId,
      title: (formTitle || '').trim(),
      details: (formDetails || '').trim(),
      severity: (formSeverity || '').trim(),
      is_active: !!formIsActive,
    };

    if (!payload.title) {
      setErrorMsg('Title is required.');
      return;
    }

    try {
      setErrorMsg('');
      if (editingWarning?.id) {
        await api.put(`/admin/warnings/${editingWarning.id}`, payload);
      } else {
        await api.post('/admin/warnings', payload);
      }
      setOpenDialog(false);
      hardReload();
    } catch (err) {
      setErrorMsg(err?.response?.data?.msg || 'Could not save warning.');
    }
  }

  async function deactivateWarning(warningId) {
    if (!warningId) return;
    try {
      setErrorMsg('');
      await api.delete(`/admin/warnings/${warningId}`);
      hardReload();
    } catch (err) {
      setErrorMsg(err?.response?.data?.msg || 'Could not disable warning.');
    }
  }

  return (
    <Box sx={{ p: { xs: 1, md: 2 }, overflowX: 'hidden' }}>
      <Box sx={{ ...headerGradientBorder, p: 1.5, mb: 1.5, maxWidth: 1300, mx: 'auto' }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 900, letterSpacing: 1, fontSize: { xs: 16, md: 18 } }}>
              ⚠️ WARNINGS CONTROL
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel>Window (days)</InputLabel>
              <Select
                label="Window (days)"
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
              >
                {DAYS_OPTIONS.map((d) => (
                  <MenuItem key={d} value={d}>
                    {d} days
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Search employee"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: { xs: '100%', sm: 240 } }}
            />

            <Button
              variant="contained"
              onClick={openCreate}
              disabled={!selectedEmployeeId}
              sx={{
                fontWeight: 900,
                borderRadius: 2,
                background: 'linear-gradient(90deg, #ec4899, #a855f7)',
              }}
            >
              + NEW WARNING
            </Button>
          </Stack>
        </Stack>

        {errorMsg ? (
          <Box sx={{ mt: 1 }}>
            <Alert severity="warning">{errorMsg}</Alert>
          </Box>
        ) : null}
      </Box>

      <Box sx={{ maxWidth: 1300, mx: 'auto' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '380px minmax(0, 1fr)' },
            gap: 1.5,
            alignItems: 'stretch',
          }}
        >
          {/* LEFT: Employees */}
          <Paper elevation={0} sx={{ borderRadius: 2, p: 1, ...headerGradientBorder, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography sx={{ fontWeight: 900, letterSpacing: 1 }}>👥 EMPLOYEES</Typography>
              <Chip size="small" label={loadingEmployees ? 'Loading…' : `${employees.length}`} sx={{ fontWeight: 900 }} />
            </Stack>

            <Divider sx={{ mb: 1 }} />

            {loadingEmployees ? (
              <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={26} />
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: { xs: 320, md: 640 }, overflowX: 'auto' }}>
                <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', minWidth: 360 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 900, width: '75%' }}>NAME</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 900, width: '25%' }}>{`LAST ${days}D`}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {employees.map((e) => {
                      const isSelected = e.id === selectedEmployeeId;
                      const count = Number(e.warning_count_window || 0);
                      return (
                        <TableRow
                          key={e.id}
                          hover
                          onClick={() => setSelectedEmployeeId(e.id)}
                          sx={{
                            cursor: 'pointer',
                            backgroundColor: isSelected ? 'rgba(168, 85, 247, 0.08)' : 'transparent',
                          }}
                        >
                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                              <Box
                                sx={{
                                  width: 30,
                                  height: 30,
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: 'rgba(236, 72, 153, 0.12)',
                                  fontSize: 16,
                                  flex: '0 0 auto',
                                }}
                              >
                                {e.avatar_emoji || '🙂'}
                              </Box>

                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  sx={{
                                    fontWeight: 900,
                                    fontSize: 13,
                                    lineHeight: 1.1,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  {e.name}
                                </Typography>
                                <Typography
                                  sx={{
                                    color: 'text.secondary',
                                    fontSize: 11,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  Group #{e.group_id ?? '-'}
                                </Typography>
                              </Box>
                            </Stack>
                          </TableCell>

                          <TableCell align="right">
                            <Chip
                              size="small"
                              label={`${count}`}
                              sx={{
                                fontWeight: 900,
                                backgroundColor: count >= 3 ? 'rgba(239, 68, 68, 0.14)' : 'rgba(34, 197, 94, 0.12)',
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {!employees.length ? (
                      <TableRow>
                        <TableCell colSpan={2}>
                          <Typography sx={{ color: 'text.secondary', fontSize: 12 }}>
                            No active employees found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          {/* RIGHT: Warnings */}
          <Paper elevation={0} sx={{ borderRadius: 2, p: 1, ...headerGradientBorder, minWidth: 0 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 900, letterSpacing: 1 }}>🧾 WARNINGS</Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: 12 }}>
                  Selected: <b>{selectedLabel}</b>
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                <Chip size="small" label={`Active: ${activeWarningsCount}`} sx={{ fontWeight: 900 }} />
                <Chip size="small" label={`Total: ${warnings.length}`} sx={{ fontWeight: 900 }} />
              </Stack>
            </Stack>
            <Divider sx={{ mb: 1 }} />

            {loadingWarnings ? (
              <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={26} />
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: { xs: 320, md: 640 }, overflowX: 'auto' }}>
                <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', minWidth: 820 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 900, width: 120 }}>SEVERITY</TableCell>

                      <TableCell sx={{ width: 260 }}>
                        <Typography sx={{ fontWeight: 900, fontSize: 12, lineHeight: 1.1 }}>
                          TITLE
                        </Typography>
                        <Typography sx={{ fontSize: 10.5, color: 'text.secondary', lineHeight: 1.1 }}>
                          (EMPLOYEE SEES)
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ width: 320 }}>
                        <Typography sx={{ fontWeight: 900, fontSize: 12, lineHeight: 1.1 }}>
                          DETAILS
                        </Typography>
                        <Typography sx={{ fontSize: 10.5, color: 'text.secondary', lineHeight: 1.1 }}>
                          (HIDDEN)
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ fontWeight: 900, width: 140 }}>STATUS</TableCell>
                      <TableCell sx={{ fontWeight: 900, width: 170 }}>CREATED</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 900, width: 200 }}>ACTIONS</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {warnings.map((w) => (
                      <TableRow key={w.id} hover>
                        <TableCell>
                          <Chip
                            size="small"
                            label={(w.severity || '').trim() ? String(w.severity).toUpperCase() : '—'}
                            sx={{ fontWeight: 900 }}
                          />
                        </TableCell>

                        <TableCell>
                          <Typography
                            sx={{
                              fontWeight: 900,
                              fontSize: 13,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {w.title}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Tooltip title="Hidden from employee">
                            <Typography
                              sx={{
                                fontSize: 12,
                                color: 'text.secondary',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {w.details ? w.details : '—'}
                            </Typography>
                          </Tooltip>
                        </TableCell>

                        <TableCell>
                          <Chip
                            size="small"
                            label={w.is_active ? '✅ ACTIVE' : '⛔ INACTIVE'}
                            sx={{
                              fontWeight: 900,
                              backgroundColor: w.is_active ? 'rgba(34, 197, 94, 0.12)' : 'rgba(148, 163, 184, 0.18)',
                            }}
                          />
                        </TableCell>

                        <TableCell>
                          <Typography sx={{ fontSize: 12 }}>{formatDateTime(w.created_at)}</Typography>
                        </TableCell>

                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ flexWrap: 'wrap' }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => openEdit(w)}
                              sx={{ fontWeight: 900, borderRadius: 2 }}
                            >
                              ✏️ EDIT
                            </Button>

                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() => deactivateWarning(w.id)}
                              sx={{ fontWeight: 900, borderRadius: 2 }}
                            >
                              🗑️ DISABLE
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}

                    {!warnings.length ? (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <Typography sx={{ color: 'text.secondary', fontSize: 12 }}>
                            No warnings for this employee.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Box>
      </Box>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>
          {editingWarning ? '✏️ EDIT WARNING' : '➕ NEW WARNING'}
        </DialogTitle>

        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 1 }}>
            This warning will be applied to:<br />
            <b style={{ fontSize: 14 }}>
              {selectedEmployee ? `${selectedEmployee.avatar_emoji || '🙂'} ${selectedEmployee.name}` : '—'}
            </b>
          </Alert>

          <Alert severity="info" sx={{ mb: 1 }}>
            Employee sees only <b>Title</b>. Details are hidden.
          </Alert>

          <Stack spacing={1.25}>
            <TextField
              label="Title (employee sees)"
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              fullWidth
            />

            <TextField
              label="Details (hidden)"
              value={formDetails}
              onChange={(e) => setFormDetails(e.target.value)}
              fullWidth
              multiline
              minRows={3}
            />

            <TextField
              label="Severity"
              placeholder="Ex: verbal, write-up, final, coaching, etc"
              value={formSeverity}
              onChange={(e) => setFormSeverity(e.target.value)}
              fullWidth
            />

            <FormControlLabel
              control={<Switch checked={formIsActive} onChange={(e) => setFormIsActive(e.target.checked)} />}
              label={formIsActive ? '✅ Active (counts)' : '⛔ Inactive (does not count)'}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 1.25 }}>
          <Button onClick={() => setOpenDialog(false)} sx={{ fontWeight: 900 }}>
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={submitDialog}
            sx={{
              fontWeight: 900,
              borderRadius: 2,
              background: 'linear-gradient(90deg, #ec4899, #a855f7)',
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
